
import * as React from 'react'
import { compose } from 'widgets-shared/core'
import { withTranslations } from 'widgets-shared/core'
import Typography from '@mui/material/Typography'
import { withStyles, createStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles';
import { PropTranslations } from 'smartstream-widgets/types/widget'
import { TranslationFunction } from 'smartstream-widgets/types/sharedModule/core'
import { getDefaultTheme } from 'smartstream-widgets/utils/getDefaultTheme'
import { LocationOnSharp } from '@mui/icons-material'
import { getFormattedDate } from '../../utils/FormatDate/formatDate'

type Props = {
  className: string,
  classes: {
    [K in string]: string
  },
  colorInverted: boolean | null,
  matchPhase: 'InPlay' | 'PreMatch' | 'Halftime' | 'PostMatch' | 'OverTime',
  matchStatus: string,
  scheduledStartTime: string,
  translations: PropTranslations,
  venueName: string,
  dateCaption: string,
  compactDateFormat: boolean,
  locale?: string
}

const styles = (theme: Theme) => createStyles({
  live: {
    alignItems: 'center',
    display: 'inline-flex',
    fontSize: 10,
    fontWeight: 'bold',
    lineHeight: 1,
    '&::before': {
      background: 'currentColor',
      borderRadius: '50%',
      content: '\'\'',
      display: 'block',
      height: '0.5em',
      marginRight: 3,
      width: '0.5em'
    }
  },
  chip: {
    background: theme.palette.error.main,
    borderRadius: '1em',
    color: theme.palette.common.white,
    padding: `0.2em 0.6em 0.3em`
  },
  inverted: {
    textTransform: 'uppercase',
    background: theme.palette.common.white,
    color: theme.palette.error.main
  },
  root: {
    alignItems: 'center',
    display: 'flex',
    fontSize: 10
  },
  dateCaption: {
    color: theme.palette.text.primary,
    lineHeight: 1.1,
    fontSize: 8
  },
  liveCaption: {
    color: theme.palette.text.primary,
    marginLeft: '1ch'
  },
  venueCaption: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    transform: 'translateY(4px)',
    color: theme.palette.grey['600'],
    fontSize: 10,
    '& svg': {
      width: 12,
      height: 12,
      marginRight: 4
    }
  },
  dateWithVenue: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  }
})

function MatchState(props: Props) {
  const {
    classes,
    className = '',
    colorInverted = false,
    matchPhase,
    matchStatus,
    scheduledStartTime,
    translations,
    venueName,
    dateCaption,
    compactDateFormat = false,
    locale = ''
  } = props

  const mapPhases = (matchPhase: string, translations: { [K in string]: string }): string => {
    switch (matchPhase) {
      case 'PreMatch':
        return translations.prematch
      case 'Halftime':
        return translations.halftime
      case 'OverTime':
        return translations.overtime
      case 'PostMatch':
        return translations.postMatch
      default:
        return matchPhase
    }
  }

  const translatedDateCaption = dateCaption == 'FINAL' ? translations.final.toUpperCase() : ''
  const translatedMatchPhase = mapPhases(matchPhase, translations)
  return (
    <>
      {(matchStatus === 'InPlay' || scheduledStartTime) &&
        <div className={`${classes.root} ${className}`} data-testid='match-state-container'>
          {
            matchStatus === 'InPlay' && (
              <>
                <Typography
                  component='span'
                  className={`${classes.live} ${colorInverted ? classes.inverted : classes.chip}`}
                >{translations.live}
                </Typography>
                {
                  (translatedMatchPhase || venueName) &&
                  <Typography className={classes.liveCaption}>
                    {translatedMatchPhase || (venueName && (`${translations.at} ${venueName}`))}
                  </Typography>
                }
              </>
            )
          }
          {
            matchStatus !== 'InPlay' && (
              <div className={`${classes.dateWithVenue}`}>
                <Typography className={classes.dateCaption}>
                  {translatedDateCaption || getFormattedDate(locale, scheduledStartTime, compactDateFormat)}
                </Typography>
                {venueName &&
                  <Typography className={classes.venueCaption}>
                    <LocationOnSharp />
                    <span>{venueName}</span>
                  </Typography>}
              </div>
            )
          }
        </div>
      }
    </>
  )
}

export default compose(
  withStyles(styles, { name: 'MatchState', defaultTheme: getDefaultTheme }),
  withTranslations((t: TranslationFunction) => {
    return {
      live: t('matchState.live.translation', {
        defaultValue: 'Live',
        context: 'Live label for game state in MatchState control'
      }),
      at: t('matchState.at.translation', {
        defaultValue: 'at',
        context: 'Preposition to refer to the venue/place of the game in MatchState control'
      }),
      atTime: t('matchState.atTime.translation', {
        defaultValue: 'at',
        context: 'Preposition to refer to the time of the game in MatchState control'
      }),
      final: t('matchState.final.translation', {
        defaultValue: 'Final',
        context: 'Denotates the end of the game.'
      }),
      halftime: t('matchState.halftime.translation', {
        defaultValue: 'Half time',
        context: 'Denotates the halftime of the game.'
      }),
      overtime: t('matchState.overtime.translation', {
        defaultValue: 'Overtime',
        context: 'Denotates the overtime of the game.'
      }),
      prematch: t('matchState.prematch.translation', {
        defaultValue: 'Prematch',
        context: 'Denotates the prematch of the game.'
      }),
    }
  })
)(MatchState)
